/**
 * RegenerationProgress Component
 *
 * Displays progress for knowledge-triggered content regeneration jobs.
 * Shows real-time progress with auto-refresh and job details.
 *
 * Part of Story 48: Prescriptive Issue Resolution
 *
 * Date: December 22, 2025
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  RefreshCw,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  Loader2,
} from 'lucide-react';
import {
  getRegenerationJobStatus,
  cancelRegenerationJob,
  type RegenerationJob,
} from '@/services/domainKnowledgeService';

interface RegenerationProgressProps {
  jobId: string;
  onComplete?: (job: RegenerationJob) => void;
  onCancel?: () => void;
  compact?: boolean;
}

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock,
  },
  running: {
    label: 'Running',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Loader2,
  },
  completed: {
    label: 'Completed',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: CheckCircle2,
  },
  completed_with_errors: {
    label: 'Completed with Errors',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: AlertCircle,
  },
  failed: {
    label: 'Failed',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: AlertCircle,
  },
  cancelled: {
    label: 'Cancelled',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: X,
  },
};

export function RegenerationProgress({
  jobId,
  onComplete,
  onCancel,
  compact = false,
}: RegenerationProgressProps) {
  const [job, setJob] = useState<RegenerationJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchJobStatus = useCallback(async () => {
    try {
      const response = await getRegenerationJobStatus(jobId);
      if (response.success && response.data) {
        setJob(response.data);
        setError(null);

        // Check if job is complete
        if (['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(response.data.status)) {
          onComplete?.(response.data);
        }
      } else {
        setError(response.error || 'Failed to fetch job status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch job status');
    } finally {
      setLoading(false);
    }
  }, [jobId, onComplete]);

  useEffect(() => {
    fetchJobStatus();

    // Poll for updates while job is active
    const interval = setInterval(() => {
      if (job && !['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(job.status)) {
        fetchJobStatus();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [fetchJobStatus, job?.status]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const response = await cancelRegenerationJob(jobId);
      if (response.success) {
        fetchJobStatus();
        onCancel?.();
      } else {
        setError(response.error || 'Failed to cancel job');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel job');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading job status...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
        <Button variant="ghost" size="sm" onClick={fetchJobStatus}>
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const isActive = ['pending', 'running'].includes(job.status);

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <StatusIcon
          className={`h-4 w-4 ${job.status === 'running' ? 'animate-spin' : ''}`}
        />
        <div className="flex-1">
          <Progress value={job.progress} className="h-2" />
        </div>
        <span className="text-sm text-muted-foreground">
          {job.completedItems}/{job.totalItems}
        </span>
        {isActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={cancelling}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            Content Regeneration
          </CardTitle>
          <Badge className={statusConfig.color}>
            <StatusIcon
              className={`h-3 w-3 mr-1 ${job.status === 'running' ? 'animate-spin' : ''}`}
            />
            {statusConfig.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {job.knowledgeTitle && (
          <div className="text-sm">
            <span className="text-muted-foreground">Knowledge: </span>
            <span className="font-medium">{job.knowledgeTitle}</span>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span>{job.progress}%</span>
          </div>
          <Progress value={job.progress} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {job.completedItems} of {job.totalItems} items
            </span>
            {job.failedItems > 0 && (
              <span className="text-destructive">
                {job.failedItems} failed
              </span>
            )}
          </div>
        </div>

        {job.estimatedCost !== undefined && (
          <div className="text-sm">
            <span className="text-muted-foreground">Est. Cost: </span>
            <span>${job.estimatedCost.toFixed(2)}</span>
          </div>
        )}

        {job.startedAt && (
          <div className="text-xs text-muted-foreground">
            Started: {new Date(job.startedAt).toLocaleString()}
          </div>
        )}

        {job.completedAt && (
          <div className="text-xs text-muted-foreground">
            Completed: {new Date(job.completedAt).toLocaleString()}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchJobStatus}
            disabled={loading}
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          {isActive && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <X className="h-3 w-3 mr-1" />
              )}
              Cancel
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * RegenerationJobsList - List of recent regeneration jobs
 */
interface RegenerationJobsListProps {
  siteId: string;
  limit?: number;
}

export function RegenerationJobsList({
  siteId,
  limit = 5,
}: RegenerationJobsListProps) {
  const [jobs, setJobs] = useState<RegenerationJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchJobs() {
      try {
        const { getRegenerationJobs } = await import('@/services/domainKnowledgeService');
        const response = await getRegenerationJobs(siteId, limit);
        if (response.success && response.data) {
          setJobs(response.data.jobs);
        }
      } catch (err) {
        console.error('Failed to fetch regeneration jobs:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchJobs();
  }, [siteId, limit]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Loading jobs...</span>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        No regeneration jobs found
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {jobs.map((job) => {
        const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
        const StatusIcon = statusConfig.icon;

        return (
          <div
            key={job.jobId}
            className="flex items-center gap-3 p-2 border rounded-md"
          >
            <StatusIcon
              className={`h-4 w-4 ${job.status === 'running' ? 'animate-spin' : ''} ${
                job.status === 'completed' ? 'text-green-600' :
                job.status === 'failed' ? 'text-red-600' :
                job.status === 'running' ? 'text-blue-600' : 'text-muted-foreground'
              }`}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {job.knowledgeTitle || 'Regeneration Job'}
              </div>
              <div className="text-xs text-muted-foreground">
                {job.completedItems}/{job.totalItems} items
              </div>
            </div>
            <Progress value={job.progress} className="w-20 h-2" />
            <Badge className={`text-xs ${statusConfig.color}`}>
              {job.progress}%
            </Badge>
          </div>
        );
      })}
    </div>
  );
}

export default RegenerationProgress;
